"use client";
import { Suspense as RouteSuspense } from 'react';
import { PageLoading as RouteLoading } from '@/app/Components/LoadingBubbles';
import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import CourseLists from '../Components/CourseLists';
import PublicCourseLists from '../Components/PublicCourseLists';
import { useAuth } from '../Components/AuthProvider';
import { useRouter, useSearchParams } from 'next/navigation';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { RouteStateFixture } from '@/app/Components/RouteStateFixture';
import { auditFixtureState } from '@/app/lib/routeStateContract';
import { SEMESTER_OPTIONS } from '@/app/lib/semesters';
import { PageLoading } from '@/app/Components/LoadingBubbles';

function CourseListsRoute() {
  const state = auditFixtureState(useSearchParams().get('auditFixture'));
  return state ? <RouteStateFixture route="course-lists" state={state} /> : <CourseListsPage />;
}

function CourseListsPage() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pendingCourseId = searchParams.get('addCourse');
  const routeQuery = searchParams.toString();
  const ownerId = user?.id ?? null;
  const completionContext = useMemo(() => ({ routeQuery, pendingCourseId, ownerId, authLoading }), [routeQuery, pendingCourseId, ownerId, authLoading]);
  const committedCompletionContext = useRef<typeof completionContext | null>(null);
  useLayoutEffect(() => {
    committedCompletionContext.current = completionContext;
    return () => {
      if (committedCompletionContext.current === completionContext) committedCompletionContext.current = null;
    };
  }, [completionContext]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      const query = new URLSearchParams(routeQuery);
      if (!SEMESTER_OPTIONS.some(({ id }) => id === query.get('semester'))) query.delete('semester');
      const destination = `/course-lists${query.size ? `?${query}` : ''}`;
      router.replace(`/login?redirect=${encodeURIComponent(destination)}`);
    }
  }, [user, authLoading, routeQuery, router]);

  // Show loading while checking authentication
  if (authLoading) {
    return <PageLoading title="Opening Course Lists" description="Loading your saved lists and public collections." />;
  }

  // Keep a visible state while the effect above redirects to sign in.
  if (!user) {
    return <PageLoading title="Redirecting to sign in" description="Course Lists needs your Scarlet Sync account." />;
  }


  return (
    <div className="min-h-full pb-8">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 rounded-feature border border-violet-100 bg-white px-5 py-4 shadow-e1 dark:border-violet-900/60 dark:bg-slate-900">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-red-600 dark:text-red-400">Course Planning</p>
              <h1 className="flex items-center text-2xl font-bold text-slate-900 dark:text-white md:text-3xl">
                <BookOpenIcon className="mr-2.5 h-7 w-7 text-red-600" />
                Course Lists
              </h1>
              <p className="max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                Build reusable shortlists for requirements, backups, electives, and shared recommendations.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 dark:border-white/10 dark:bg-white/5">Saved lists</span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 dark:border-white/10 dark:bg-white/5">Public collections</span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 dark:border-white/10 dark:bg-white/5">Scheduling reuse</span>
            </div>
          </div>
        </div>

        <div className="space-y-12">
          {/* User's Course Lists */}
          <section>
            <CourseLists pendingCourseId={pendingCourseId} onCourseAdded={() => {
              if (committedCompletionContext.current !== completionContext || completionContext.authLoading || !completionContext.ownerId || !completionContext.pendingCourseId) return;
              const query = new URLSearchParams(completionContext.routeQuery);
              if (query.get('addCourse') !== completionContext.pendingCourseId) return;
              // This completion owns only its committed pending query; issued child writes are untouched.
              committedCompletionContext.current = null;
              query.delete('addCourse');
              if (!SEMESTER_OPTIONS.some(({ id }) => id === query.get('semester'))) query.delete('semester');
              router.replace(`/course-lists${query.size ? `?${query}` : ''}`);
            }} />
          </section>

          {/* Public Course Lists Section */}
          <section>
            <PublicCourseLists />
          </section>
        </div>
      </div>
    </div>
  );
}

// Query-dependent interactive content owns its loading boundary.
export default function RoutePage() { return <RouteSuspense fallback={<RouteLoading title="Preparing your workspace" />}><CourseListsRoute /></RouteSuspense>; }
